<template>
  <div class="min-h-screen p-4">
    <!-- Header -->
    <header class="sticky top-0 z-50 mb-6 border-b border-gray-800/80 bg-[#0a0e27]/90 backdrop-blur-sm">
      <div class="max-w-7xl mx-auto">
        <div class="flex items-start justify-between gap-4 py-4">
          <div class="flex-1">
            <a
              href=""
              class="inline-flex items-center gap-3 no-underline"
              @click.prevent="reloadApp"
            >
              <img
                src="/favicon.svg"
                alt="Safetrax logo"
                class="h-[3.25rem] w-auto"
              >
              <span class="flex h-[3.25rem] flex-col justify-between">
                <h1 class="text-3xl leading-tight font-bold text-white">
                  Safetrax
                </h1>
                <p class="text-gray-400 leading-tight">
                  Configuration utility
                </p>
              </span>
            </a>
          </div>
          
          <!-- Connection Control -->
          <div class="flex flex-col items-end gap-1">
            <div class="flex items-center gap-3">
              <span class="text-sm text-gray-500 whitespace-nowrap">Tech support 0800 110 820</span>
              <button
                class="btn whitespace-nowrap"
                :class="connectionState === 'connected' ? 'btn-danger' : 'btn-success'"
                :disabled="connectionState === 'connecting' || isUiLocked"
                @click="handleConnect"
              >
                {{ connectButtonText }}
              </button>
            </div>
            <div class="text-sm text-right">
              <div v-if="waitingForWakeup" class="text-xs text-warning-500 mt-1 max-w-xs">
                Waiting for device wake-up (press button / turn key on)
              </div>
              <div v-if="lastError" class="text-xs text-danger-600 mt-1 max-w-xs">
                {{ lastError }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto">
      <!-- WebSerial API Check -->
      <div v-if="!isWebSerialSupported" class="card bg-warning-50 border-warning-500 mb-4">
        <div class="flex items-start gap-3">
          <svg class="w-6 h-6 text-warning-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <h3 class="font-bold text-warning-900 mb-1">WebSerial Not Supported</h3>
            <p class="text-warning-800">
              Your browser does not support the WebSerial API. Please use Chrome, Edge, or Opera to access serial devices.
            </p>
          </div>
        </div>
      </div>

      <!-- Device asleep/wake prompt -->
      <div v-if="waitingForWakeup" class="card bg-warning-50 border-warning-500 mb-4">
        <div class="flex items-start gap-3">
          <svg class="w-6 h-6 text-warning-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l2.5 2.5M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z" />
          </svg>
          <div>
            <h3 class="font-bold text-warning-900 mb-1">Device is asleep</h3>
            <p class="text-warning-800">
              Waiting for IMU response before initialization. Press the device button or turn the key on.
            </p>
          </div>
        </div>
      </div>

      <!-- Dashboard with Test Buttons integrated -->
      <div class="panel">
        <StatusDashboard />
      </div>

      <!-- Configuration Panel -->
      <div class="panel">
        <ConfigPanel />
      </div>

      <!-- Console Log -->
      <div class="panel">
        <ConsoleLog />
      </div>
    </main>

    <!-- Footer -->
    <footer class="mt-8 text-center text-sm text-gray-500">
      <p>Safetrax WebConfig v1.0.0 | © {{ currentYear }}</p>
    </footer>

    <!-- Global UI lock overlay during calibration flow -->
    <div
      v-if="isUiLocked"
      class="fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm"
      aria-hidden="true"
    ></div>

    <CalibrationDialog
      v-if="showCalibrationConfirmDialog"
      mode="confirm"
      title="Start calibration"
      description="Ensure device is level and engine is off. Continue with calibration?"
      @cancel="handleCalibrationCancel"
      @confirm="handleCalibrationConfirm"
    />

    <CalibrationDialog
      v-if="isCalibrationInProgress"
      mode="progress"
      title="Calibration in progress, please wait"
      :progress-percent="calibrationProgressPercent"
      :seconds-remaining="calibrationSecondsRemaining"
    />

    <CalibrationDialog
      v-if="showCalibrationCompleteDialog"
      mode="complete"
      title="Calibration complete"
      @ok="handleCalibrationCompleteOk"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue';
import { useDeviceStore } from './stores/device.js';
import { useConfigStore } from './stores/config.js';
import StatusDashboard from './components/StatusDashboard.vue';
import ConfigPanel from './components/ConfigPanel.vue';
import ConsoleLog from './components/ConsoleLog.vue';
import CalibrationDialog from './components/CalibrationDialog.vue';

const deviceStore = useDeviceStore();
const configStore = useConfigStore();

// Check WebSerial API support
const isWebSerialSupported = ref(true);

const currentYear = computed(() => new Date().getFullYear());

// Connection state
const connectionState = computed(() => deviceStore.connectionState);
const lastError = computed(() => deviceStore.lastError);
const waitingForWakeup = computed(() => deviceStore.waitingForWakeup);
const showCalibrationConfirmDialog = computed(() => deviceStore.showCalibrationConfirmDialog);
const isCalibrationInProgress = computed(() => deviceStore.isCalibrationInProgress);
const showCalibrationCompleteDialog = computed(() => deviceStore.showCalibrationCompleteDialog);
const isUiLocked = computed(() => (
  showCalibrationConfirmDialog.value
  || isCalibrationInProgress.value
  || showCalibrationCompleteDialog.value
));

const CALIBRATION_DURATION_MS = 15000;
const calibrationStartedAt = ref(null);
const calibrationNow = ref(Date.now());
let calibrationProgressTimer = null;

const calibrationElapsedMs = computed(() => {
  if (!calibrationStartedAt.value) return 0;
  return Math.max(0, calibrationNow.value - calibrationStartedAt.value);
});

const calibrationProgressPercent = computed(() => {
  const percent = (calibrationElapsedMs.value / CALIBRATION_DURATION_MS) * 100;
  return Math.min(100, Math.max(0, percent));
});

const calibrationSecondsRemaining = computed(() => {
  const remainingMs = Math.max(0, CALIBRATION_DURATION_MS - calibrationElapsedMs.value);
  return Math.ceil(remainingMs / 1000);
});

const connectButtonText = computed(() => {
  switch (connectionState.value) {
    case 'connecting': return 'Connecting...';
    case 'connected': return 'Disconnect';
    default: return 'Connect';
  }
});

function reloadApp() {
  window.location.reload();
}

async function handleConnect() {
  if (isUiLocked.value) return;

  if (connectionState.value === 'connected') {
    // Disconnect
    await deviceStore.disconnect();
  } else {
    // Connect
    try {
      const connected = await deviceStore.connect();
      
      if (connected) {
        // Load presets after successful connection
        await configStore.loadPresets();
      }
    } catch (err) {
      console.error('Connection failed:', err);
    }
  }
}

function stopCalibrationProgressTimer() {
  if (calibrationProgressTimer) {
    clearInterval(calibrationProgressTimer);
    calibrationProgressTimer = null;
  }
}

watch(isCalibrationInProgress, (inProgress) => {
  stopCalibrationProgressTimer();

  if (!inProgress) {
    calibrationStartedAt.value = null;
    return;
  }

  calibrationStartedAt.value = Date.now();
  calibrationNow.value = calibrationStartedAt.value;

  calibrationProgressTimer = setInterval(() => {
    calibrationNow.value = Date.now();
    if (calibrationStartedAt.value && calibrationNow.value - calibrationStartedAt.value >= CALIBRATION_DURATION_MS) {
      stopCalibrationProgressTimer();
      deviceStore.completeCalibrationTimer();
    }
  }, 100);
});

function handleCalibrationCompleteOk() {
  deviceStore.acknowledgeCalibrationComplete();
}

function handleCalibrationCancel() {
  deviceStore.cancelCalibrationRequest();
}

async function handleCalibrationConfirm() {
  try {
    await deviceStore.startCalibration();
  } catch (err) {
    console.error('Calibration failed:', err);
    deviceStore.cancelCalibrationRequest();
  }
}

onMounted(() => {
  // Check if WebSerial API is available
  isWebSerialSupported.value = 'serial' in navigator;
  
  if (!isWebSerialSupported.value) {
    console.error('WebSerial API is not supported in this browser');
  }
});

onBeforeUnmount(() => {
  stopCalibrationProgressTimer();
});
</script>
